import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AutoAwesomeRoundedIcon from "@mui/icons-material/AutoAwesomeRounded";
import MicRoundedIcon from "@mui/icons-material/MicRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import KeyboardRoundedIcon from "@mui/icons-material/KeyboardRounded";
import ReplayRoundedIcon from "@mui/icons-material/ReplayRounded";
import PowerSettingsNewRoundedIcon from "@mui/icons-material/PowerSettingsNewRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import { ownerApi } from "../../../../api/owner";
import { formatApiError } from "../../../../api/client";
import { useOwnerVoiceOperator } from "../voice/OwnerVoiceOperatorContext";
import { surfaceSx } from "./ownerCenterUi";
import { explainOwnerJarvisReasons } from "../jarvis/ownerJarvisReasonCodes";
import {
  isExplicitOwnerJarvisBacklogCaptureRequest,
  isOwnerJarvisBacklogAcknowledgement,
} from "../jarvis/capabilities/ownerJarvisImprovementCapabilities";


const CLASSIC_JARVIS_MUTATION_SCOPES = [
  "today",
  "commitments",
  "projects",
  "people",
  "waiting",
  "calendar",
];

const JARVIS_TURN_STORAGE_KEY = "wp-owner-jarvis-turn-v4";
const JARVIS_TURN_TTL_MS = 12 * 60 * 60 * 1000;
const JARVIS_COLLAPSED_STORAGE_KEY = "wp-owner-jarvis-collapsed-v1";

function readCollapsedPreference() {
  if (typeof window === "undefined") return null;
  try {
    const value = window.localStorage.getItem(JARVIS_COLLAPSED_STORAGE_KEY);
    if (value == null) return null;
    return value === "1";
  } catch { return null; }
}

function persistCollapsedPreference(value) {
  if (typeof window === "undefined") return;
  try { window.localStorage.setItem(JARVIS_COLLAPSED_STORAGE_KEY, value ? "1" : "0"); }
  catch { /* localStorage opcional */ }
}

function readStoredTurn() {
  if (typeof window === "undefined") return null;
  try {
    const parsed = JSON.parse(window.sessionStorage.getItem(JARVIS_TURN_STORAGE_KEY) || "null");
    if (!parsed?.turn || !parsed.savedAt || Date.now() - Number(parsed.savedAt) > JARVIS_TURN_TTL_MS) return null;
    return parsed.turn;
  } catch {
    return null;
  }
}

function persistTurn(turn) {
  if (typeof window === "undefined") return;
  try {
    if (!turn) window.sessionStorage.removeItem(JARVIS_TURN_STORAGE_KEY);
    else window.sessionStorage.setItem(JARVIS_TURN_STORAGE_KEY, JSON.stringify({ turn, savedAt: Date.now() }));
  } catch { /* sessionStorage opcional */ }
}

function projectPath(initiativeId) {
  return `/dashboard/owner/proyectos/${encodeURIComponent(String(initiativeId || ""))}`;
}

function spokenTurn(turn) {
  const why = explainOwnerJarvisReasons(turn?.reasons);
  return [turn?.reply, why, turn?.question].filter(Boolean).join(" ").trim();
}

function shouldBreakOutToClassicJarvis(raw) {
  const text = String(raw || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const explicitNavigation = /\b(?:abr\w*|mostr\w*|mir\w*|llev\w*|ver|ir)\b/.test(text)
    && /\b(?:ficha|proyecto|proyectos|cliente|clientes|persona|rutina|rutinas|dia|fecha|movimientos|finanzas|calendario|agenda)\b/.test(text);
  const directProjectNote = /\b(?:anot\w*|apunt\w*|agreg\w*|carg\w*|registr\w*)\b/.test(text)
    && /\b(?:proyecto|frente|iniciativa)\b/.test(text);
  const directFinanceView = /\b(?:mostr\w*|mir\w*|ver|list\w*)\b/.test(text)
    && /\b(?:entrada|entradas|ingreso|ingresos|pago|pagos|cobro|cobros|movimiento|movimientos)\b/.test(text);
  const directCapture = /\b(?:anot\w*|carg\w*|registr\w*|agreg\w*)\b/.test(text)
    && /\b(?:gasto|ingreso|movimiento|transferencia|compra|tarea|seguimiento|recordatorio|propuesta|presupuesto)\b/.test(text);
  const directContract = /\b(?:crear|crea|armar|arma|definir|define|nuevo|nueva|hacer)\b/.test(text)
    && /\b(?:contrato|acuerdo base)\b/.test(text);
  return explicitNavigation || directProjectNote || directFinanceView || directCapture || directContract;
}

function isDayReviewRequest(raw) {
  const text = String(raw || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return /\b(?:que|qué)\s+(?:hice|hicimos)\s+hoy\b/.test(text)
    || /\b(?:saber|registrar|repasar|revisar|reconstruir|reconstruyamos|repasemos)\b.*\b(?:lo que )?(?:hice|hicimos|paso)\b.*\bhoy\b/.test(text)
    || /\b(?:repasemos|revisemos|reconstruyamos)\b.*\b(?:el|mi)\s+dia\b/.test(text);
}

function shouldUseClassicJarvis(raw) {
  const text = String(raw || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  if (isDayReviewRequest(raw)) return false;
  const dayFlow = /\b(me puse a|me estoy|estoy (?:cocinando|comiendo|leyendo|programando|trabajando|estudiando|meditando|entrenando)|arranque a|empece a|termine|pare|deje de|me levante|me desperte|me acabo de levantar|acabo de levantarme|desayune|almorce|cene|comi|me bane|me acoste|que estoy haciendo|que tengo corriendo)\b/.test(text);
  const todayPendings = /\b(pendientes?|tareas?|acciones?)\b.*\b(hoy|para hacer|para avanzar)\b|\bhoy\b.*\b(pendientes?|tareas?|acciones?)\b/.test(text);
  return dayFlow || todayPendings || shouldBreakOutToClassicJarvis(raw)
    || /\b(?:crear|crea|nuevo|nueva|registrar|registra|anota que|recorda que|guardar recuerdo|que hice|que paso|recuerdos|recordame|stock|cuanto trabaje|que sabes de mi|que tengo hoy|como viene mi dia)\b/.test(text);
}


const DAY_SELECTION_STORAGE_KEY = "wp-owner-day-pending-selection-until";

function daySelectionIsActive() {
  if (typeof window === "undefined") return false;
  try {
    const until = Number(window.sessionStorage.getItem(DAY_SELECTION_STORAGE_KEY) || 0);
    if (until > Date.now()) return true;
    if (until) window.sessionStorage.removeItem(DAY_SELECTION_STORAGE_KEY);
  } catch { /* optional */ }
  return false;
}

function shouldAlwaysDelegateDayFlow(raw) {
  const text = String(raw || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
  const directDayFlow = /\b(me puse a|me estoy|estoy (?:cocinando|comiendo|leyendo|programando|trabajando|estudiando|meditando|entrenando)|arranque a|empece a|termine|pare|deje de|me levante|me desperte|me acabo de levantar|acabo de levantarme|desayune|almorce|cene|comi|me bane|me acoste|que estoy haciendo|que tengo corriendo)\b/.test(text);
  const todayPendings = /\b(pendientes?|tareas?|acciones?)\b.*\b(hoy|para hacer|para avanzar)\b|\bhoy\b.*\b(pendientes?|tareas?|acciones?)\b/.test(text);
  const selection = daySelectionIsActive() && (/^(?:el\s+)?\d{1,2}$/.test(text) || /\b(me interesa|me pongo con|arranco con|empiezo con|quiero hacer|voy con)\b/.test(text));
  return directDayFlow || todayPendings || selection;
}

function shouldAlwaysUseClassicJarvis(raw) {
  const text = String(raw || "").toLowerCase();
  return isOwnerJarvisBacklogAcknowledgement(raw)
    || isExplicitOwnerJarvisBacklogCaptureRequest(raw)
    || /\b(jarvis lab|lista de fixes|lista de mejoras|backlog|mej[-\s]?\d+|fix[-\s]?\d+)\b/.test(text)
    || (/\bjarvis\b/.test(text) && /\b(deberia|debería|tendria|tendría|no esta|no está|no hace|falla|error|mejora)\b/.test(text));
}

const CLASSIC_HOME_QUERY_TTL_MS = 5 * 60 * 1000;

function isRootNavigationTurn(turn) {
  return String(turn?.session?.kind || "") === "navigation"
    && String(turn?.session?.branch || "root") === "root";
}

function isExplicitTreeNavigation(raw) {
  const text = String(raw || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  return /^(inicio|menu|menu principal|volver|atras|opciones|mi dia|hoy|proyectos?|clientes?|prospectos?|dinero|plata|finanzas|vida|rutinas?|capturar|jarvis lab|fixes|errores|mejoras)$/.test(text.trim());
}

function shouldUseClassicHomeQuery(raw) {
  const text = String(raw || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
  const asksExpense = /\b(cuanto|cuanta|cuantos|cuantas)\b/.test(text)
    && /\b(gaste|gastado|gasto|pague|pagado|pago|pagar|se fue)\b/.test(text);
  // Mostrar ingresos es una consulta financiera aunque el usuario no diga
  // literalmente "categoría". Ej.: "mostrame el ingreso de Juan Napoleón".
  const asksIncomeSet = /\b(?:mostr\w*|ver|mir\w*|cuanto|cuanta|cuantos|cuantas)\b/.test(text)
    && /\b(entrada|entradas|ingreso|ingresos|pago|pagos|cobro|cobros)\b/.test(text);
  const asksAccount = /\b(cuanto|cuanta|saldo)\b/.test(text)
    && /\b(plata|saldo|deberia tener|debo tener|tengo en|hay en|caja|cuenta)\b/.test(text);
  return asksExpense || asksIncomeSet || asksAccount;
}

function improvementReviewTurn(review) {
  const id = String(review?.externalId || "Mejora").trim();
  const title = String(review?.title || "mejora ejecutada").trim();
  const guide = String(review?.reviewGuide || "").trim();
  return {
    handled: true,
    reply: `${id} figura como HECHO_POR_BOT: ${title}.`,
    question: guide ? `¿Cómo quedó en uso real? ${guide}` : "¿Cómo quedó en uso real? Si está bien la cierro; si sigue mal, decime qué falta y la reactivo con tu devolución.",
    choices: [
      { value: "review_ok", label: "Quedó bien" },
      { value: "review_bad", label: "Sigue mal" },
      { value: "review_later", label: "Después" },
    ],
    session: { version: 1, kind: "improvement_review", stage: "decision", externalId: id, title },
    highlightSection: "today-jarvis",
    signal: { label: id, kind: "improvement_review" },
  };
}

export default function OwnerJarvis2({ currency = "ARS", onChanged, compact = false }) {
  const navigate = useNavigate();
  const voice = useOwnerVoiceOperator();
  const [turn, setTurn] = React.useState(() => readStoredTurn());
  const [text, setText] = React.useState("");
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [typing, setTyping] = React.useState(false);
  const [quickQueriesOpen, setQuickQueriesOpen] = React.useState(false);
  const [collapsed, setCollapsed] = React.useState(() => {
    const saved = readCollapsedPreference();
    const mobileDefault = typeof window !== "undefined" && window.matchMedia?.("(max-width:599.95px)")?.matches;
    return saved == null ? (Boolean(compact) || Boolean(mobileDefault)) : saved;
  });
  const turnRef = React.useRef(null);
  const voiceRef = React.useRef(voice);
  const onChangedRef = React.useRef(onChanged);
  const classicHomeQueryUntilRef = React.useRef(0);
  turnRef.current = turn;
  voiceRef.current = voice;
  onChangedRef.current = onChanged;

  const executeUi = React.useCallback((ui) => {
    if (!ui?.surface) return;
    const token = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const state = { ownerJarvisUi: { ...ui, token } };
    if (ui.surface === "project360" && ui.initiativeId) {
      navigate(projectPath(ui.initiativeId), { state });
      return;
    }
    if (ui.surface === "client" && ui.clientId) {
      navigate(`/dashboard/owner/clientes?cliente=${encodeURIComponent(String(ui.clientId))}`, { state });
      return;
    }
    if (ui.surface === "owner_home") {
      const allowed = new Set(["finance", "consumption", "client", "proposal", "commitment", "routine", "worklog", "recurring", "interaction", "initiative", "import", "inboxProcess"]);
      const action = allowed.has(String(ui.form || "")) ? String(ui.form) : "";
      navigate(action ? `/dashboard/owner?jarvisAction=${encodeURIComponent(action)}` : "/dashboard/owner", { state });
      return;
    }
    if (ui.surface === "route" && String(ui.path || "").startsWith("/dashboard/owner")) {
      navigate(String(ui.path), { state });
    }
  }, [navigate]);

  const applyResponse = React.useCallback(async (response, { speak = false } = {}) => {
    const next = response?.turn || null;
    if (!next) return { handled: false };
    persistTurn(next);
    turnRef.current = next;
    setTurn(next);
    setError("");
    if (next.refreshOwner) {
      await onChangedRef.current?.(CLASSIC_JARVIS_MUTATION_SCOPES);
      try { window.dispatchEvent(new CustomEvent("owner:jarvis-mutated", { detail: { scopes: CLASSIC_JARVIS_MUTATION_SCOPES } })); } catch { /* noop */ }
    }
    const message = spokenTurn(next);
    const activeVoice = voiceRef.current;
    if (speak && message) {
      activeVoice?.guideToSection?.(next.highlightSection || "today-jarvis", message, {
        listenAfter: Boolean(activeVoice?.handsFree || activeVoice?.alwaysOnEnabled),
        force: true,
        scroll: next.highlightSection && next.highlightSection !== "today-jarvis",
      });
    }
    if (next.ui) executeUi(next.ui);
    return {
      handled: next.handled !== false,
      message,
      highlightSection: next.highlightSection || "today-jarvis",
    };
  }, [executeUi]);

  const shouldDelegateHomeQueryToClassic = React.useCallback((raw) => {
    if (isExplicitTreeNavigation(raw)) {
      classicHomeQueryUntilRef.current = 0;
      return false;
    }

    // Si una consulta financiera clásica abrió una conversación (por ejemplo
    // elegir una categoría por número), sus respuestas deben seguir llegando
    // al mismo runtime aunque el Jarvis visual conserve otra rama de navegación.
    if (Date.now() < classicHomeQueryUntilRef.current) return true;

    // Las consultas financieras son atajos globales: pueden nacer desde Inicio,
    // un proyecto o cualquier otra rama. Al detectarlas abrimos continuidad antes
    // de que el árbol contextual pueda apropiarse de la siguiente respuesta.
    if (shouldUseClassicHomeQuery(raw)) {
      classicHomeQueryUntilRef.current = Date.now() + CLASSIC_HOME_QUERY_TTL_MS;
      return true;
    }
    return false;
  }, []);

  const send = React.useCallback(async (message, { speak = false } = {}) => {
    const clean = String(message || "").trim();
    setLoading(true);
    setError("");
    try {
      const response = await ownerApi.jarvisTurn({
        message: clean,
        session: turnRef.current?.session || null,
        currency,
        currentPath: `${window.location.pathname || ""}${window.location.search || ""}`,
      });
      return await applyResponse(response, { speak });
    } catch (err) {
      setError(formatApiError(err, "Jarvis no pudo procesar esa respuesta."));
      return { handled: true, message: "No pude procesarlo ahora. Probá otra vez." };
    } finally {
      setLoading(false);
    }
  }, [applyResponse, currency]);

  const restart = React.useCallback(async ({ speak = null } = {}) => {
    classicHomeQueryUntilRef.current = 0;
    persistTurn(null);
    turnRef.current = null;
    setTurn(null);
    setLoading(true);
    setError("");
    try {
      const response = await ownerApi.jarvisTurn({ message: "", session: null, currency });
      const shouldSpeak = speak == null ? Boolean(voiceRef.current?.handsFree || voiceRef.current?.alwaysOnEnabled) : Boolean(speak);
      await applyResponse(response, { speak: shouldSpeak });
    } catch (err) {
      setError(formatApiError(err, "No pude iniciar Jarvis."));
    } finally {
      setLoading(false);
    }
  }, [applyResponse, currency]);

  React.useEffect(() => {
    const onConversationReset = () => {
      classicHomeQueryUntilRef.current = 0;
      persistTurn(null);
      turnRef.current = null;
      setTurn(null);
      setError("");
      setQuickQueriesOpen(false);
      if (/^\/dashboard\/owner\/?$/.test(window.location.pathname || "")) {
        window.setTimeout(() => restart({ speak: false }), 0);
      }
    };
    window.addEventListener("owner:jarvis-conversation-reset", onConversationReset);
    return () => window.removeEventListener("owner:jarvis-conversation-reset", onConversationReset);
  }, [restart]);

  React.useEffect(() => {
    if (turnRef.current) { setLoading(false); return; }
    if (compact) { setLoading(false); return; }
    restart();
  }, [compact, restart]);

  React.useEffect(() => {
    if (!voice?.registerConversationHandler) return undefined;
    return voice.registerConversationHandler(async ({ raw }) => {
      const sessionKind = String(turnRef.current?.session?.kind || "");
      // Durante la apertura diaria, respuestas como “estoy trabajando” pertenecen
      // al ciclo que Jarvis acaba de preguntar. Si las delegamos al runtime clásico,
      // el bloque puede empezar pero la apertura queda colgada en `current_activity`.
      if (["daily_cycle", "day_guardian"].includes(sessionKind) && !shouldBreakOutToClassicJarvis(raw) && !shouldAlwaysUseClassicJarvis(raw)) {
        return send(raw, { speak: false });
      }
      if (shouldAlwaysDelegateDayFlow(raw)) return { handled: false };
      if (shouldAlwaysUseClassicJarvis(raw)) return { handled: false };
      // Un flujo clásico pendiente vive en el runtime global del workspace.
      // Puede haber empezado antes de navegar a Movimientos/Proyecto, así que
      // no dependemos de la memoria local de este OwnerJarvis2 para continuarlo.
      if (voice?.priorityConversationActive?.()) return { handled: false };
      // Evaluar primero la delegación financiera: además de rutear la primera
      // frase, deja una ventana de continuidad para respuestas cortas como "3".
      if (shouldDelegateHomeQueryToClassic(raw)) return { handled: false };
      if (shouldBreakOutToClassicJarvis(raw)) return { handled: false };
      if (!turnRef.current?.session && shouldUseClassicJarvis(raw)) return { handled: false };
      return send(raw, { speak: false });
    });
  }, [send, shouldDelegateHomeQueryToClassic, voice?.priorityConversationActive, voice?.registerConversationHandler]);

  React.useEffect(() => {
    let active = true;
    let firstTimer = null;
    let pollTimer = null;
    const poll = async () => {
      if (!active || document.visibilityState !== "visible") return;
      const kind = String(turnRef.current?.session?.kind || "");
      if (kind && kind !== "idle") return;
      try {
        const response = await ownerApi.nextJarvisBacklogReview({ markAsked: true });
        const review = response?.review;
        if (!active || !review) return;
        if (turnRef.current?.session?.kind === "improvement_review" && turnRef.current?.session?.externalId === review.externalId) return;
        const next = improvementReviewTurn(review);
        persistTurn(next);
        turnRef.current = next;
        setTurn(next);
        const words = spokenTurn(next);
        const activeVoice = voiceRef.current;
        if (words && activeVoice?.alwaysOnEnabled) activeVoice?.say?.(words, { listenAfter: true, force: true });
      } catch {
        // La revisión automática no debe interrumpir Owner si Sheets está sin conexión.
      }
    };
    firstTimer = window.setTimeout(poll, 2500);
    pollTimer = window.setInterval(poll, 4 * 60 * 1000);
    return () => {
      active = false;
      window.clearTimeout(firstTimer);
      window.clearInterval(pollTimer);
    };
  }, []);

  const submitText = async () => {
    const clean = text.trim();
    if (!clean || loading) return;
    setText("");
    // Si hay una operación global pendiente (lote → contrato, confirmación, etc.),
    // el texto también debe volver al dispatcher clásico aunque este componente
    // se haya remontado después de navegar.
    const priorityConversation = Boolean(voice?.priorityConversationActive?.());
    const delegateHomeQuery = shouldDelegateHomeQueryToClassic(clean);
    const sessionKind = String(turnRef.current?.session?.kind || "");
    if (["daily_cycle", "day_guardian"].includes(sessionKind) && !shouldBreakOutToClassicJarvis(clean) && !shouldAlwaysUseClassicJarvis(clean)) {
      await send(clean, { speak: Boolean(voice?.handsFree) });
      return;
    }
    if ((shouldAlwaysUseClassicJarvis(clean) || priorityConversation || delegateHomeQuery || shouldUseClassicJarvis(clean)) && voice?.sendText) {
      voice.sendText(clean);
      return;
    }
    await send(clean, { speak: Boolean(voice?.handsFree) });
  };

  const hardRestart = async () => {
    if (loading) return;
    const onHome = /^\/dashboard\/owner\/?$/.test(window.location.pathname || "");
    if (voice?.resetOwnerConversation) {
      await voice.resetOwnerConversation({ navigateHome: false, announce: false });
      if (!onHome) await restart({ speak: false });
      return;
    }
    await restart({ speak: false });
  };

  const goHomeAndReset = async () => {
    if (loading) return;
    if (voice?.resetOwnerConversation) {
      await voice.resetOwnerConversation({ navigateHome: true, announce: true });
      return;
    }
    await send("inicio", { speak: false });
  };

  const choose = async (choice) => {
    if (loading) return;
    classicHomeQueryUntilRef.current = 0;
    await send(choice.value, { speak: Boolean(voice?.handsFree) });
  };

  const talk = () => {
    const words = spokenTurn(turn) || "Decime qué querés resolver.";
    if (voice?.say) voice.say(words, { listenAfter: true, force: true });
    else voice?.startGlobalConversation?.();
  };

  const togglePower = () => {
    if (voice?.alwaysOnEnabled) voice?.disableAlwaysOn?.();
    else voice?.enableAlwaysOn?.({ greet: true });
  };

  const toggleCollapsed = () => {
    setCollapsed((value) => {
      const next = !value;
      persistCollapsedPreference(next);
      if (next) {
        setTyping(false);
        setQuickQueriesOpen(false);
      }
      return next;
    });
  };

  const applyQuickQueryTemplate = (template) => {
    setText(String(template || ""));
    setTyping(true);
    setQuickQueriesOpen(false);
  };

  const quickQueryExamples = [
    { label: "Gastos por categoría", template: "¿Cuánto gasté en [categoría] este mes?" },
    { label: "Ingresos por categoría", template: "Mostrame los ingresos de la categoría [categoría] de este mes" },
    { label: "Saldo de cuenta", template: "¿Cuánto tengo en [cuenta]?" },
  ];

  const sessionKind = String(turn?.session?.kind || "");
  const navigationPath = Array.isArray(turn?.navigation?.path) && turn.navigation.path.length
    ? turn.navigation.path
    : sessionKind === "project_review"
      ? [
          "Inicio",
          "Proyectos",
          turn?.session?.projectName || "Proyecto",
          ...(String(turn?.session?.stage || "").startsWith("monthly_") ? ["Mensualidad"] : []),
          ...(String(turn?.session?.stage || "").startsWith("contract_") ? ["Contrato / cuotas"] : []),
        ]
      : sessionKind === "task_planning"
        ? ["Inicio", "Proyectos", turn?.session?.projectName || "Proyecto", "Tarea"]
        : sessionKind === "daily_cycle"
          ? ["Inicio", "Mi día"]
          : sessionKind === "day_guardian"
            ? ["Inicio", "Mi día", "Repaso"]
          : sessionKind === "improvement_review"
            ? ["Inicio", "Jarvis Lab"]
            : ["Inicio"];
  const contextualKeywords = Array.isArray(turn?.navigation?.keywords) && turn.navigation.keywords.length
    ? turn.navigation.keywords
    : sessionKind === "project_review" && String(turn?.session?.stage || "").startsWith("monthly_")
      ? ["cambiar monto", "día de cobro", "billing", "próximos cobros", "volver", "inicio"]
      : sessionKind === "project_review" && String(turn?.session?.stage || "").startsWith("contract_")
        ? ["editar contrato", "cuotas semanales", "cuotas mensuales", "próximos cobros", "volver", "inicio"]
        : ["project_review", "task_planning"].includes(sessionKind)
          ? ["estado", "pendientes", "cobros", "mensualidad", "contrato", "volver", "inicio"]
          : sessionKind === "daily_cycle"
        ? ["hecha", "más tarde", "agenda", "volver", "inicio"]
        : sessionKind === "improvement_review"
          ? ["quedó bien", "sigue mal", "después", "inicio"]
          : [];
  const canGoBack = navigationPath.length > 1 || Boolean(turn?.session);
  const currentLocationLabel = navigationPath.slice(-2).join(" › ") || "Inicio";

  if (collapsed) {
    return (
      <Box data-owner-jarvis-section="today-jarvis" sx={{ ...surfaceSx, px: 1.05, py: .7, background: "linear-gradient(145deg, rgba(243,90,58,.075), rgba(255,255,255,0) 60%)" }}>
        <Stack direction="row" spacing={.75} alignItems="center">
          <Box sx={{ width: 30, height: 30, borderRadius: "50%", bgcolor: "primary.main", color: "primary.contrastText", display: "grid", placeItems: "center", flex: "0 0 auto" }}>
            {loading ? <CircularProgress size={15} color="inherit" /> : <AutoAwesomeRoundedIcon sx={{ fontSize: 17 }} />}
          </Box>
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Stack direction="row" spacing={.55} alignItems="baseline" flexWrap="wrap" useFlexGap>
              <Typography sx={{ fontWeight: 1000, fontSize: 11.5, color: "primary.main", letterSpacing: ".06em", textTransform: "uppercase" }}>Jarvis</Typography>
              <Typography sx={{ color: "text.secondary", fontWeight: 850, fontSize: 11.2 }}>{currentLocationLabel}</Typography>
            </Stack>
            <Typography noWrap sx={{ mt: .05, color: "text.secondary", fontSize: 10.7 }}>{turn?.question || turn?.reply || "La conversación sigue activa aunque ocultes el panel."}</Typography>
          </Box>
          {turn?.signal?.label ? <Chip size="small" label={turn.signal.label} variant="outlined" sx={{ display: { xs: "none", sm: "inline-flex" }, maxWidth: 130, fontWeight: 850 }} /> : null}
          <Tooltip title="Mostrar Jarvis"><IconButton onClick={toggleCollapsed} sx={{ width: 34, height: 34 }}><ExpandMoreRoundedIcon /></IconButton></Tooltip>
        </Stack>
      </Box>
    );
  }

  return (
    <Box
      data-owner-jarvis-section="today-jarvis"
      sx={{
        ...surfaceSx,
        minHeight: compact ? "auto" : { xs: 330, sm: 360, md: 390 },
        p: compact ? { xs: 1, sm: 1.15 } : { xs: 1.4, sm: 1.8, md: 2.2 },
        display: "flex",
        flexDirection: "column",
        overflow: "hidden",
        background: "linear-gradient(145deg, rgba(243,90,58,.095), rgba(255,255,255,0) 48%)",
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Box sx={{ width: 48, height: 48, borderRadius: "50%", bgcolor: "primary.main", color: "primary.contrastText", display: "grid", placeItems: "center", flex: "0 0 auto", boxShadow: "0 10px 28px rgba(243,90,58,.22)" }}>
          {loading ? <CircularProgress size={22} color="inherit" /> : <AutoAwesomeRoundedIcon />}
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography sx={{ fontWeight: 1000, fontSize: 12, color: "primary.main", letterSpacing: ".08em", textTransform: "uppercase" }}>Jarvis</Typography>
          {!compact ? <Typography sx={{ color: "text.secondary", fontWeight: 750, fontSize: 12.5 }}>Te saco contexto · ordeno el proyecto · te digo qué sigue</Typography> : <Typography sx={{ color: "text.secondary", fontWeight: 750, fontSize: 11.5 }}>Sigo con la misma conversación mientras mirás la ficha.</Typography>}
        </Box>
        {turn?.signal?.label ? <Chip size="small" label={turn.signal.label} variant="outlined" sx={{ maxWidth: 160, fontWeight: 850 }} /> : null}
        <Tooltip title="Ocultar Jarvis"><IconButton onClick={toggleCollapsed} sx={{ width: 42, height: 42 }}><ExpandLessRoundedIcon /></IconButton></Tooltip>
        <Tooltip title={voice?.alwaysOnEnabled ? "Apagar Jarvis" : "Encender Jarvis"}>
          <span>
            <IconButton
              onClick={togglePower}
              disabled={!voice?.speechSupported}
              color={voice?.alwaysOnEnabled ? "primary" : "default"}
              sx={{ width: 42, height: 42 }}
            >
              <PowerSettingsNewRoundedIcon />
            </IconButton>
          </span>
        </Tooltip>
        <Tooltip title="Reiniciar conversación"><span><IconButton onClick={hardRestart} disabled={loading} sx={{ width: 42, height: 42 }}><ReplayRoundedIcon /></IconButton></span></Tooltip>
      </Stack>

      <Stack direction={{ xs: "column", sm: "row" }} spacing={.55} alignItems={{ sm: "center" }} sx={{ mt: .8 }}>
        <Stack direction="row" spacing={.4} alignItems="center" flexWrap="wrap" useFlexGap sx={{ minWidth: 0, flex: 1 }}>
          {navigationPath.map((item, index) => (
            <React.Fragment key={`${item}-${index}`}>
              {index ? <Typography sx={{ color: "text.disabled", fontSize: 11, fontWeight: 900 }}>›</Typography> : null}
              <Typography sx={{ color: index === navigationPath.length - 1 ? "text.primary" : "text.secondary", fontSize: 11.5, fontWeight: index === navigationPath.length - 1 ? 1000 : 800 }}>{item}</Typography>
            </React.Fragment>
          ))}
        </Stack>
        <Stack direction="row" spacing={.35}>
          {canGoBack ? <Button size="small" onClick={() => send("volver", { speak: false })} disabled={loading} sx={{ minWidth: 0, px: .75, textTransform: "none", fontWeight: 900, fontSize: 11 }}>Volver</Button> : null}
          {navigationPath.length > 1 ? <Button size="small" onClick={goHomeAndReset} disabled={loading} sx={{ minWidth: 0, px: .75, textTransform: "none", fontWeight: 900, fontSize: 11 }}>Inicio</Button> : null}
        </Stack>
      </Stack>

      {contextualKeywords.length ? (
        <Typography sx={{ mt: .35, color: "text.secondary", fontSize: 10.8, lineHeight: 1.35 }}>
          Decí: {contextualKeywords.slice(0, 8).map((word) => `“${word}”`).join(" · ")}
        </Typography>
      ) : null}
      {isRootNavigationTurn(turn) ? (
        <Box sx={{ mt: .45 }}>
          <Button
            size="small"
            variant={quickQueriesOpen ? "contained" : "outlined"}
            onClick={() => setQuickQueriesOpen((value) => !value)}
            disabled={loading}
            sx={{ minHeight: 30, px: .9, borderRadius: 2, textTransform: "none", fontWeight: 950, fontSize: 11.2 }}
          >
            Consultas rápidas
          </Button>
          {quickQueriesOpen ? (
            <Box sx={{ mt: .55, p: .75, border: "1px solid", borderColor: "divider", borderRadius: 2.2, bgcolor: "action.hover" }}>
              <Typography sx={{ color: "text.secondary", fontSize: 10.7, lineHeight: 1.35, mb: .55 }}>
                Tocá un ejemplo para llevarlo al teclado. Reemplazá [categoría] o [cuenta] y enviáselo a Jarvis. Estas consultas viven sólo en Inicio.
              </Typography>
              <Stack direction="row" spacing={.45} flexWrap="wrap" useFlexGap>
                {quickQueryExamples.map((item) => (
                  <Button
                    key={item.label}
                    size="small"
                    variant="text"
                    onClick={() => applyQuickQueryTemplate(item.template)}
                    sx={{ minHeight: 30, px: .75, justifyContent: "flex-start", textTransform: "none", fontWeight: 900, fontSize: 10.9 }}
                  >
                    {item.label}
                  </Button>
                ))}
              </Stack>
              <Typography sx={{ mt: .45, color: "text.secondary", fontSize: 10.4 }}>
                Ej.: “Mostrame los ingresos de la categoría ventas” → Jarvis calcula el total y abre ese lote filtrado para revisarlo o cambiar sus vínculos.
              </Typography>
            </Box>
          ) : null}
        </Box>
      ) : null}

      {error ? <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert> : null}

      <Box sx={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", py: compact ? { xs: .8, sm: 1 } : { xs: 1.6, sm: 2.2 } }}>
        {turn?.reply ? (
          <Typography sx={{ color: "text.secondary", fontWeight: 800, fontSize: { xs: 13, sm: 14.5 }, lineHeight: 1.4, mb: .45 }}>
            {turn.reply}
          </Typography>
        ) : null}
        {explainOwnerJarvisReasons(turn?.reasons) ? (
          <Typography sx={{ color: "text.secondary", fontWeight: 750, fontSize: { xs: 12, sm: 13 }, lineHeight: 1.35, mb: .55 }}>
            {explainOwnerJarvisReasons(turn.reasons)}
          </Typography>
        ) : null}
        <Typography sx={{ fontWeight: 1000, fontSize: compact ? { xs: 18, sm: 20 } : { xs: 24, sm: 30, md: 34 }, letterSpacing: "-.04em", lineHeight: 1.08, maxWidth: 820 }}>
          {turn?.question || (loading ? "Estoy mirando qué necesita tu atención…" : "¿Qué querés resolver?")}
        </Typography>

        {turn?.choices?.length ? (
          <Box sx={{ display: "grid", gridTemplateColumns: compact ? { xs: "repeat(2,minmax(0,1fr))", sm: "repeat(3,minmax(0,1fr))" } : { xs: "1fr 1fr", sm: "repeat(2,minmax(0,1fr))", md: "repeat(3,minmax(0,1fr))" }, gap: .75, mt: compact ? .9 : 1.5, maxWidth: compact ? "none" : 760 }}>
            {turn.choices.slice(0, 8).map((choice) => (
              <Button
                key={`${choice.value}-${choice.label}`}
                onClick={() => choose(choice)}
                disabled={loading}
                variant="outlined"
                sx={{ minHeight: compact ? 40 : { xs: 52, sm: 58 }, borderRadius: 2.6, justifyContent: "flex-start", px: compact ? 1 : 1.35, textTransform: "none", fontWeight: 1000, fontSize: compact ? 11.5 : { xs: 12.5, sm: 14.5 }, textAlign: "left" }}
              >
                {choice.label}
              </Button>
            ))}
          </Box>
        ) : null}
      </Box>

      <Stack spacing={.7}>
        <Stack direction="row" spacing={.65}>
          <Button
            fullWidth
            onClick={talk}
            disabled={!voice?.speechSupported || loading}
            variant="contained"
            startIcon={<MicRoundedIcon />}
            sx={{ minHeight: compact ? 42 : 54, borderRadius: 2.8, textTransform: "none", fontWeight: 1000, fontSize: compact ? 13 : 15 }}
          >
            {voice?.isListening ? "Te escucho…" : (voice?.alwaysOnEnabled ? "Hablar con Jarvis" : "Responder hablando")}
          </Button>
          <Tooltip title={typing ? "Ocultar teclado" : "Escribir"}>
            <span>
              <IconButton onClick={() => setTyping((value) => !value)} disabled={loading} sx={{ width: compact ? 42 : 54, height: compact ? 42 : 54, border: "1px solid", borderColor: "divider", borderRadius: 2.8 }}>
                <KeyboardRoundedIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>

        {typing ? (
          <Stack direction="row" spacing={.65} alignItems="center">
            <TextField
              autoFocus
              fullWidth
              size="small"
              value={text}
              disabled={loading}
              onChange={(event) => setText(event.target.value)}
              onKeyDown={(event) => { if (event.key === "Enter" && !event.shiftKey) { event.preventDefault(); submitText(); } }}
              placeholder="Escribile a Jarvis…"
              sx={{ "& .MuiInputBase-root": { minHeight: 48, borderRadius: 2.6, fontSize: 14, fontWeight: 750 } }}
            />
            <IconButton onClick={submitText} disabled={!text.trim() || loading} color="primary" sx={{ width: 48, height: 48 }}><SendRoundedIcon /></IconButton>
          </Stack>
        ) : null}
      </Stack>
    </Box>
  );
}
